/**
 * RETAIL PRODUCT FINDER MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// LocationsTab — the retail location list: search, filters, stat tiles, row
// actions, and the two geocoding actions.
//
// Ported from the source app. Adapted for Brand Command:
//   • services take plain args and are scoped by RLS, so no callerEmail prop;
//   • `canEdit` comes from the workspace role rather than an MSAL permission
//     set (the route is already workspace-admin gated; this is belt-and-braces
//     for the day a viewer role lands);
//   • the empty-state copy points at System Settings → Sync Settings instead of
//     the OneDrive Data Source tab, which did not port.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  listLocations, deleteLocation, updateLocation, geocodePending, applyZipFallback,
} from "../services/retailLocationService.js";
import { formatAddress, fmtDate } from "./constants.js";
import { loadProductVocabulary, buildProductVocabulary, offCatalogProducts } from "../utils/productCatalog.js";
import LocationEditorModal from "./LocationEditorModal.jsx";

const STATUS_FILTERS = [
  { value: "all",      label: "All" },
  { value: "active",   label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const GEO_BADGES = {
  ok:          { label: "Mapped",      cls: "rpf-badge-on" },
  manual:      { label: "Manual",      cls: "rpf-badge-on" },
  approximate: { label: "ZIP area",    cls: "rpf-badge-warn" },
  pending:     { label: "Pending",     cls: "rpf-badge-warn" },
  failed:      { label: "Not mapped",  cls: "rpf-badge-off" },
};

export default function LocationsTab({ canEdit }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editor, setEditor] = useState({ open: false, location: null });
  const [busyId, setBusyId] = useState(null);
  const [geoBusy, setGeoBusy] = useState(null);
  // The tenant's product vocabulary (backlog items 100/101). Loaded once, from
  // SHARED reference data, and used for two things: the editor's picker and the
  // "off catalog" drift signal below. Never fatal — see loadProductVocabulary().
  const [vocab, setVocab] = useState(() => buildProductVocabulary({}));

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setLocations(await listLocations());
    } catch (e) {
      setError(e.message || "Failed to load retail locations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    let live = true;
    loadProductVocabulary().then(v => { if (live) setVocab(v); });
    return () => { live = false; };
  }, []);

  const states = useMemo(
    () => Array.from(new Set(locations.map(l => l.state).filter(Boolean))).sort(),
    [locations],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return locations.filter(l => {
      if (stateFilter !== "all" && l.state !== stateFilter) return false;
      if (statusFilter === "active"   && !l.is_active) return false;
      if (statusFilter === "inactive" &&  l.is_active) return false;
      if (!q) return true;
      const hay = [
        l.name, l.chain, l.city, l.state, l.postal_code,
        l.address_line1, (l.products || []).join(" "),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [locations, search, stateFilter, statusFilter]);

  const activeCount = useMemo(() => locations.filter(l => l.is_active).length, [locations]);
  const missingGeo  = useMemo(
    () => locations.filter(l => l.is_active && (l.latitude == null || l.longitude == null)).length,
    [locations],
  );
  const pendingGeo  = useMemo(
    () => locations.filter(l => l.geocode_status === "pending").length,
    [locations],
  );
  // Census could not match these. They have a ZIP we can fall back to, so they
  // can still be placed on the map (approximately) instead of being invisible.
  const fallbackable = useMemo(
    () => locations.filter(l => l.geocode_status === "failed" && l.postal_code).length,
    [locations],
  );

  // Backlog item 100 — make the drift VISIBLE. Every distinct product name on a
  // LIVE location that the tenant's catalog has never heard of is a name the
  // public widget is offering shoppers as a filter. Counted per name, not per
  // store, because one bad spelling on forty doors is one problem to fix.
  const offCatalog = useMemo(() => {
    if (!vocab?.canonicalByKey?.size) return { names: [], stores: 0 };
    const names = new Map();
    let stores = 0;
    for (const l of locations) {
      if (!l.is_active) continue;
      const bad = offCatalogProducts(l.products, vocab);
      if (!bad.length) continue;
      stores++;
      for (const n of bad) names.set(n, (names.get(n) || 0) + 1);
    }
    return { names: [...names.entries()].sort((a, b) => b[1] - a[1]), stores };
  }, [locations, vocab]);

  // Flat lookup for the per-row chips. Covers hidden stores too — an admin
  // fixing drift wants to see it wherever it is, not only where it's published.
  const offSet = useMemo(() => new Set(
    vocab?.canonicalByKey?.size
      ? locations.flatMap(l => offCatalogProducts(l.products, vocab))
      : [],
  ), [locations, vocab]);

  const handleToggleActive = async (loc) => {
    setBusyId(loc.id);
    setError(null);
    try {
      const saved = await updateLocation(loc.id, { is_active: !loc.is_active });
      setLocations(rows => rows.map(r => (r.id === saved.id ? saved : r)));
    } catch (e) {
      setError(e.message || "Failed to update location.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(`Permanently delete "${loc.name}"? This removes it from the website map.`)) return;
    setBusyId(loc.id);
    setError(null);
    try {
      await deleteLocation(loc.id);
      setLocations(rows => rows.filter(r => r.id !== loc.id));
    } catch (e) {
      setError(e.message || "Failed to delete location.");
    } finally {
      setBusyId(null);
    }
  };

  // Loops the batched geocoder until nothing is pending. Batched because a
  // full store book is far more than one edge-function invocation can finish.
  const handleGeocodePending = async () => {
    setGeoBusy({ processed: 0, matched: 0, remaining: pendingGeo });
    setError(null);
    try {
      let processed = 0, matched = 0, remaining = Infinity;
      while (remaining > 0) {
        const r = await geocodePending();
        processed += r.processed; matched += r.matched; remaining = r.remaining;
        setGeoBusy({ processed, matched, remaining });
        if (r.processed === 0) break; // nothing geocodable left (e.g. no ZIP)
      }
      await refresh();
    } catch (e) {
      setError(e.message || "Geocoding failed.");
    } finally {
      setGeoBusy(null);
    }
  };

  const handleZipFallback = async () => {
    if (!window.confirm(
      `Place ${fallbackable.toLocaleString()} unmatched locations at the centre of their ZIP code?\n\n` +
      `They will appear on the website map about 1-3 miles from their real address, ` +
      `marked "ZIP area" so you can tell them apart. The address shown to customers stays exact.`
    )) return;
    setGeoBusy({ processed: 0, matched: 0, remaining: 0 });
    setError(null);
    try {
      await applyZipFallback();
      await refresh();
    } catch (e) {
      setError(e.message || "Could not apply the ZIP fallback.");
    } finally {
      setGeoBusy(null);
    }
  };

  const handleSaved = (saved) => {
    setLocations(rows => {
      const exists = rows.some(r => r.id === saved.id);
      return exists ? rows.map(r => (r.id === saved.id ? saved : r)) : [saved, ...rows];
    });
    setEditor({ open: false, location: null });
  };

  return (
    <>
      <div className="rpf-stats">
        <div className="rpf-stat"><div className="rpf-stat-num">{loading ? "—" : locations.length}</div><div className="rpf-stat-label">Total Locations</div></div>
        <div className="rpf-stat"><div className="rpf-stat-num">{loading ? "—" : activeCount}</div><div className="rpf-stat-label">Live on Website</div></div>
        <div className="rpf-stat"><div className="rpf-stat-num">{loading ? "—" : states.length}</div><div className="rpf-stat-label">States Covered</div></div>
        <div className={`rpf-stat${!loading && missingGeo ? " rpf-stat-warn" : ""}`}>
          <div className="rpf-stat-num">{loading ? "—" : missingGeo}</div>
          <div className="rpf-stat-label">Missing Coordinates</div>
        </div>
        <div
          className={`rpf-stat${!loading && offCatalog.names.length ? " rpf-stat-warn" : ""}`}
          title="Product names on live stores that don't exist in your product catalog. Shoppers see them in the website filter."
        >
          <div className="rpf-stat-num">{loading ? "—" : offCatalog.names.length}</div>
          <div className="rpf-stat-label">Products Off Catalog</div>
        </div>
      </div>

      {!loading && offCatalog.names.length > 0 && (
        <div className="rpf-drift">
          <strong>{offCatalog.names.length}</strong> product name
          {offCatalog.names.length === 1 ? "" : "s"} on {offCatalog.stores} live store
          {offCatalog.stores === 1 ? "" : "s"} {offCatalog.names.length === 1 ? "isn’t" : "aren’t"} in
          your product catalog — shoppers see {offCatalog.names.length === 1 ? "it" : "them"} in the
          website filter:
          <span className="rpf-chips rpf-drift-chips">
            {offCatalog.names.slice(0, 8).map(([n, c]) => (
              <span key={n} className="rpf-chip rpf-chip-warn">{n} <em>×{c}</em></span>
            ))}
            {offCatalog.names.length > 8 && (
              <span className="rpf-chip">+{offCatalog.names.length - 8} more</span>
            )}
          </span>
          <span className="rpf-drift-fix">
            Fix them on the store, or add them in System Settings → Products.
          </span>
        </div>
      )}

      <div className="rpf-toolbar">
        <input
          className="rpf-search"
          placeholder="Search name, chain, city, ZIP, or product…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select aria-label="Filter by state" value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
          <option value="all">All States</option>
          {states.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="rpf-segment" role="group" aria-label="Filter by status">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              type="button"
              aria-pressed={statusFilter === f.value}
              className={`rpf-seg-btn${statusFilter === f.value ? " is-active" : ""}`}
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="rpf-spacer" />
        {canEdit && pendingGeo > 0 && (
          <button className="rpf-btn" onClick={handleGeocodePending} disabled={!!geoBusy}>
            {geoBusy
              ? `Geocoding… ${geoBusy.matched}/${geoBusy.processed} (${geoBusy.remaining} left)`
              : `Geocode ${pendingGeo} pending`}
          </button>
        )}
        {canEdit && pendingGeo === 0 && fallbackable > 0 && (
          <button className="rpf-btn" onClick={handleZipFallback} disabled={!!geoBusy}>
            {geoBusy ? "Placing…" : `Place ${fallbackable.toLocaleString()} unmatched by ZIP`}
          </button>
        )}
        <button className="rpf-btn" onClick={refresh} disabled={loading}>Refresh</button>
        {canEdit && (
          <button className="rpf-btn rpf-btn-primary" onClick={() => setEditor({ open: true, location: null })}>
            + Add Location
          </button>
        )}
      </div>

      {error && <div className="rpf-alert">{error}</div>}

      <div className="rpf-table-wrap">
        <table className="rpf-table">
          <thead>
            <tr>
              <th>Location</th>
              <th>Address</th>
              <th>Coordinates</th>
              <th>Products Ordered</th>
              <th>Status</th>
              {/* Backlog item 202 — `last_sold_on` was selected and never
                  rendered, so the grid answered "when did this ROW change"
                  when the question is "when did this store last order". Both
                  now show; only one of them is evidence. */}
              <th title="When this store last ordered. Only depletion-derived stores carry it.">Last ordered</th>
              <th>Updated</th>
              {canEdit && <th className="rpf-actions-col">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={canEdit ? 8 : 7} className="rpf-loading">
                <div className="rpf-loading-inner">
                  <div className="rpf-spinner" />
                  <div className="rpf-loading-title">Loading retail locations…</div>
                  <div className="rpf-loading-sub">This can take a few seconds — a full store book runs to thousands of rows.</div>
                </div>
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={canEdit ? 8 : 7} className="rpf-empty">
                {locations.length === 0
                  ? "No retail locations yet. Add the first one, or connect a source in System Settings → Sync Settings."
                  : "No locations match the current filters."}
              </td></tr>
            )}
            {!loading && filtered.map(loc => {
              const geo = GEO_BADGES[loc.geocode_status] || null;
              return (
                <tr key={loc.id} className={busyId === loc.id ? "is-busy" : ""}>
                  <td>
                    <div className="rpf-name">{loc.name}</div>
                    {loc.chain && <div className="rpf-sub">{loc.chain}</div>}
                  </td>
                  <td>
                    <div>{formatAddress(loc) || "—"}</div>
                  </td>
                  <td>
                    {loc.latitude != null && loc.longitude != null
                      ? <span className="rpf-mono">{Number(loc.latitude).toFixed(4)}, {Number(loc.longitude).toFixed(4)}</span>
                      : <span className="rpf-warn-text">Not mapped</span>}
                    {geo && <div><span className={`rpf-badge ${geo.cls}`}>{geo.label}</span></div>}
                  </td>
                  <td>
                    {(loc.products || []).length
                      ? <div className="rpf-chips">{loc.products.map((p, i) => (
                          <span
                            key={i}
                            className={`rpf-chip${offSet.has(p) ? " rpf-chip-warn" : ""}`}
                            title={offSet.has(p) ? "Not in your product catalog" : undefined}
                          >{p}</span>
                        ))}</div>
                      : <span className="rpf-sub">—</span>}
                  </td>
                  <td>
                    <span className={`rpf-badge${loc.is_active ? " rpf-badge-on" : " rpf-badge-off"}`}>
                      {loc.is_active ? "Live" : "Hidden"}
                    </span>
                  </td>
                  <td className="rpf-sub">{fmtDate(loc.last_sold_on)}</td>
                  <td className="rpf-sub">{fmtDate(loc.updated_at)}</td>
                  {canEdit && (
                    <td className="rpf-actions-col">
                      <button className="rpf-btn rpf-btn-sm" onClick={() => setEditor({ open: true, location: loc })} disabled={busyId === loc.id}>Edit</button>
                      <button className="rpf-btn rpf-btn-sm" onClick={() => handleToggleActive(loc)} disabled={busyId === loc.id}>
                        {loc.is_active ? "Hide" : "Show"}
                      </button>
                      <button className="rpf-btn rpf-btn-sm rpf-btn-danger" onClick={() => handleDelete(loc)} disabled={busyId === loc.id}>Delete</button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editor.open && (
        <LocationEditorModal
          location={editor.location}
          vocab={vocab}
          onClose={() => setEditor({ open: false, location: null })}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
